import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { serverTypeLabel, serverTypeShort } from '@/utils/server-type';
import ConfirmActionModal, { type ConfirmActionConfig } from '@/components/ui/ConfirmActionModal';
import LegsPanel from '@/components/linear/LegsPanel';
import { pagePreview, statusMessageText, apiErrorText, closeAfterRun } from '@/utils/channel-actions';
import {
  Plus, Edit, Trash2, X, Check, Loader2, Play, Square, Download,
  RefreshCw, Settings, Tv, Clock, Server, HelpCircle, CheckSquare, Globe, Upload, Search,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  linearService, LinearChannel, WizardChannel,
  xaccelService, XaccelServer, resolutionsService, Resolution,
  cdnService, CdnName,
  getErrorMessage, getErrorCode,
} from '@/services';
import { xaccelProxyService, XaccelProxy } from '@/services/xaccel-proxy.service';
import { parseServerIds, parseProxyByResolution, getProxyForServer, PerServerProxyMap } from '@/utils/server-ids';
import { XACCEL_TAB_REQUEST_TIMEOUT_MS } from '@/utils/server-reads';
import { bulkRetryChannels } from '@/utils/comp-channels';
import type { BulkPlanServer, BulkServerResult } from '@/services/linear.service';

type ModalTab = 'general' | 'streaming' | 'restart' | 'xaccel';

function HintTooltip({ text }: { text: string }) {
  return (
    <span className="relative group/hint inline-flex ml-1 align-middle">
      <HelpCircle size={14} className="text-dark-500 hover:text-dark-300 cursor-help" />
      <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2.5 py-1.5
        bg-dark-100 text-white text-xs rounded-md shadow-lg
        opacity-0 invisible group-hover/hint:opacity-100 group-hover/hint:visible
        pointer-events-none transition-all duration-150 z-50 w-max max-w-[220px] text-center">
        {text}
      </span>
    </span>
  );
}

export default function LinearChannels() {
  const { t } = useTranslation();
  /** Toast text of an API error: known codes translated, otherwise the server's message. */
  const errText = (error: unknown) => apiErrorText({ code: getErrorCode(error), message: getErrorMessage(error) }, t);

  // Data
  const [channels, setChannels] = useState<LinearChannel[]>([]);
  const [xaccelServers, setXaccelServers] = useState<XaccelServer[]>([]);
  const [resolutions, setResolutions] = useState<Resolution[]>([]);
  const [cdnNames, setCdnNames] = useState<CdnName[]>([]);
  const [cdnPreferredSearch, setCdnPreferredSearch] = useState('');
  const [cdnBlacklistSearch, setCdnBlacklistSearch] = useState('');
  const [xaccelProxies, setXaccelProxies] = useState<XaccelProxy[]>([]);
  const [proxyAssignments, setProxyAssignments] = useState<PerServerProxyMap>({});
  const [isSyncingProxies, setIsSyncingProxies] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingChannel, setEditingChannel] = useState<LinearChannel | null>(null);
  const [activeTab, setActiveTab] = useState<ModalTab>('general');
  const [isSaving, setIsSaving] = useState(false);

  // Edit form state
  const [formData, setFormData] = useState({
    name: '',
    dazn_asset_id: '',
    xaccel_channel_name: '',
    xaccel_server_id: '',
    server_ids: [] as number[],
    xaccel_stream_enable: false,
    cdn_blacklist: [] as string[],
    cdn_preferred: [] as string[],
    verify_age: false,
    pin_protect: false,
    auto_restart_enabled: false,
    restart_times: '[]',
    video_map_overrides: {} as Record<string, string>,
    max_restart_attempts: 3,
    restart_cooldown_minutes: 10,
  });
  const [selectedResolutions, setSelectedResolutions] = useState<number[]>([]);
  const resolutionSaveTimer = useRef<ReturnType<typeof setTimeout>>();

  // Auto-save resolutions on toggle (debounced)
  const toggleResolution = useCallback((resId: number) => {
    if (!editingChannel) return;
    setSelectedResolutions(prev => {
      const next = prev.includes(resId)
        ? prev.filter(id => id !== resId)
        : [...prev, resId];

      // Debounce: save after 300ms to batch rapid clicks
      clearTimeout(resolutionSaveTimer.current);
      resolutionSaveTimer.current = setTimeout(async () => {
        try {
          await linearService.updateResolutions(editingChannel.id, next);
        } catch {
          toast.error(t('linear.resolutionSaveError'));
        }
      }, 300);

      return next;
    });
  }, [editingChannel]);

  // Xaccel channels tab: the legs panel owns its data; the page only bumps it after its own actions.
  const [legActionTick, setLegActionTick] = useState(0);
  const bumpLegState = () => setLegActionTick((n) => n + 1);
  // Number of DB legs the panel last saw (the channel-wide tools below it need at least one).
  const [legCount, setLegCount] = useState(0);
  // Preview + explicit confirmation of bulk / destructive / restart actions (nothing of the kind runs on one click).
  const [confirmConfig, setConfirmConfig] = useState<ConfirmActionConfig | null>(null);

  // Wizard (4-step bulk configure)
  const [showWizard, setShowWizard] = useState(false);
  const [wizardChannels, setWizardChannels] = useState<WizardChannel[]>([]);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardSelected, setWizardSelected] = useState<Set<string>>(new Set());
  const [wizardStep, setWizardStep] = useState(1);
  const [wizardPrefix, setWizardPrefix] = useState('');
  const [wizardSeparator, setWizardSeparator] = useState<'_' | ' ' | '-'>('_');
  const [wizardXaccelServer, setWizardXaccelServer] = useState<number | null>(null);
  const [wizardServerIds, setWizardServerIds] = useState<number[]>([]);
  const [wizardResolutionIds, setWizardResolutionIds] = useState<number[]>([]);
  const [wizardProxyByRes, setWizardProxyByRes] = useState<PerServerProxyMap>({});
  const [wizardAutoRestart, setWizardAutoRestart] = useState(false);
  const [wizardRestartTimes, setWizardRestartTimes] = useState<string[]>([]);
  const [wizardCreateXaccel, setWizardCreateXaccel] = useState(true);
  const [wizardExecuting, setWizardExecuting] = useState(false);
  const [wizardResult, setWizardResult] = useState<{ imported: number; xaccel_created: number; xaccel_failed: number; results?: BulkServerResult[] } | null>(null);
  // Step 3 preview: the REAL state of each selected server for the legs the new channels would get (dry run).
  const [wizardPlan, setWizardPlan] = useState<BulkPlanServer[] | null>(null);
  const [wizardPlanLoading, setWizardPlanLoading] = useState(false);
  const [wizardRetrying, setWizardRetrying] = useState(false);

  // Action states
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});
  const [bulkLoading, setBulkLoading] = useState<string | null>(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      const [channelsData, serversData, resData, cdnData, proxiesData] = await Promise.all([
        linearService.list(),
        xaccelService.list(),
        resolutionsService.list(),
        cdnService.list().catch(() => [] as CdnName[]),
        xaccelProxyService.listProxies().catch(() => [] as XaccelProxy[]),
      ]);
      setChannels(channelsData);
      setXaccelServers(serversData);
      setResolutions(resData);
      setCdnNames(cdnData);
      setXaccelProxies(proxiesData);

      // Auto-sync proxies if empty and xaccel servers exist
      if (proxiesData.length === 0 && serversData.length > 0) {
        try {
          setIsSyncingProxies(true);
          const syncResult = await xaccelProxyService.syncProxies();
          if (syncResult.synced > 0) {
            const newProxies = await xaccelProxyService.listProxies();
            setXaccelProxies(newProxies);
          }
        } catch {
          // Ignore auto-sync errors
        } finally {
          setIsSyncingProxies(false);
        }
      }
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Manual sync proxies from Xaccel servers
  const handleSyncProxies = async () => {
    setIsSyncingProxies(true);
    try {
      await xaccelProxyService.syncProxies();
      const newProxies = await xaccelProxyService.listProxies();
      setXaccelProxies(newProxies);
      // Show count of proxies for the currently selected server in edit modal
      const currentServerId = formData.xaccel_server_id ? Number(formData.xaccel_server_id) : null;
      const visibleCount = currentServerId
        ? newProxies.filter(p => p.xaccelServerId === currentServerId).length
        : newProxies.length;
      toast.success(t('proxies.synced', { count: visibleCount }));
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    } finally {
      setIsSyncingProxies(false);
    }
  };

  // Auto-poll Xaccel real status every 10s when running channels exist
  const hasRunningRef = useRef(false);
  hasRunningRef.current = channels.some(c => c.status === 'running');

  useEffect(() => {
    const interval = setInterval(() => {
      if (hasRunningRef.current) {
        fetchData();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Stats
  const runningCount = channels.filter(c => c.status === 'running').length;
  const stoppedCount = channels.filter(c => c.status === 'stopped').length;
  const errorCount = channels.filter(c => c.status === 'error').length;

  // === WIZARD (4-step bulk configure) ===
  const openWizard = async () => {
    setShowWizard(true);
    setWizardLoading(true);
    setWizardStep(1);
    setWizardSelected(new Set());
    setWizardPrefix('');
    setWizardSeparator('_');
    setWizardProxyByRes({});
    setWizardAutoRestart(false);
    setWizardRestartTimes([]);
    setWizardCreateXaccel(true);
    setWizardExecuting(false);
    setWizardResult(null);

    // Set default server and resolutions
    const defaultServer = xaccelServers.find(s => s.is_default) || xaccelServers[0];
    setWizardXaccelServer(defaultServer?.id || null);
    setWizardServerIds(defaultServer ? [defaultServer.id] : []);
    setWizardResolutionIds(resolutions.map(r => r.id));

    try {
      const data = await linearService.wizard();
      setWizardChannels(data);
      // Pre-select non-imported channels
      const newSet = new Set<string>();
      data.filter(ch => !ch.already_imported).forEach(ch => newSet.add(ch.id));
      setWizardSelected(newSet);
    } catch (error) {
      toast.error(errText(error) || t('linear.loadingDaznChannels'));
    } finally {
      setWizardLoading(false);
    }
  };

  const closeWizard = () => {
    if (wizardExecuting) return; // Prevent closing during execution
    setShowWizard(false);
  };

  const toggleWizardChannel = (id: string) => {
    setWizardSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const selectAllWizard = () => {
    const newSet = new Set<string>();
    wizardChannels.filter(ch => !ch.already_imported).forEach(ch => newSet.add(ch.id));
    setWizardSelected(newSet);
  };

  const deselectAllWizard = () => setWizardSelected(new Set());

  const toggleWizardResolution = (resId: number) => {
    setWizardResolutionIds(prev =>
      prev.includes(resId) ? prev.filter(id => id !== resId) : [...prev, resId]
    );
  };

  const addWizardRestartTime = () => {
    setWizardRestartTimes(prev => [...prev, '06:00']);
  };

  const updateWizardRestartTime = (idx: number, value: string) => {
    setWizardRestartTimes(prev => prev.map((t, i) => i === idx ? value : t));
  };

  const removeWizardRestartTime = (idx: number) => {
    setWizardRestartTimes(prev => prev.filter((_, i) => i !== idx));
  };

  // Build preview channel name
  const buildPreviewName = (channelName: string, resName: string) => {
    const prefix = wizardPrefix.trim();
    const baseName = prefix ? `${prefix}${wizardSeparator}${channelName}` : channelName;
    const sanitized = baseName
      .replace(/[^a-zA-Z0-9\s_\[\]()-]/g, '')
      .replace(/\s+/g, wizardSeparator === ' ' ? ' ' : wizardSeparator);
    return `${sanitized}${wizardSeparator}${resName}`;
  };

  const wizardRequest = () => ({
    channels: wizardChannels
      .filter(ch => wizardSelected.has(ch.id) && !ch.already_imported)
      .map(ch => ({
        dazn_asset_id: ch.id,
        name: ch.title,
        subtitle: ch.subtitle || undefined,
        image_url: ch.image || undefined,
      })),
    xaccel_server_id: wizardServerIds[0],
    server_ids: wizardServerIds,
    resolution_ids: wizardResolutionIds,
    prefix: wizardPrefix || undefined,
    separator: wizardSeparator,
    proxy_by_resolution: Object.keys(wizardProxyByRes).length > 0 ? wizardProxyByRes : undefined,
    auto_restart_enabled: wizardAutoRestart,
    restart_times: wizardAutoRestart && wizardRestartTimes.length > 0 ? wizardRestartTimes : undefined,
    create_xaccel_channels: wizardCreateXaccel,
  });

  // Step 3 (summary): read the real state of the selected servers before anything is created.
  const loadWizardPlan = async () => {
    const req = wizardRequest();
    if (!wizardCreateXaccel || req.channels.length === 0 || wizardServerIds.length === 0) { setWizardPlan(null); return; }
    setWizardPlanLoading(true);
    try {
      setWizardPlan(await linearService.bulkPlan(req));
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
      setWizardPlan(null);
    } finally {
      setWizardPlanLoading(false);
    }
  };
  useEffect(() => {
    if (showWizard && wizardStep === 3) loadWizardPlan();
    else setWizardPlan(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWizard, wizardStep, wizardCreateXaccel]);

  // Retry of the failed legs (only on the servers where they failed / could not be reached).
  const retryWizardFailed = async () => {
    if (!wizardResult?.results) return;
    const channelsToRetry = bulkRetryChannels(wizardResult.results);
    if (channelsToRetry.length === 0) return;
    setWizardRetrying(true);
    try {
      const r = await linearService.bulkCreateLegs(channelsToRetry);
      setWizardResult((prev) => prev && ({ ...prev, xaccel_created: prev.xaccel_created + r.created, xaccel_failed: r.failed, results: r.results }));
      if (r.failed > 0) toast.error(t('linear.wizardRetryPartial', { created: r.created, failed: r.failed }));
      else toast.success(t('linear.wizardRetryDone', { count: r.created }));
      fetchData();
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    } finally {
      setWizardRetrying(false);
    }
  };

  // Execute bulk configure
  const executeWizard = async () => {
    const req = wizardRequest();
    if (req.channels.length === 0 || wizardServerIds.length === 0 || wizardResolutionIds.length === 0) {
      return;
    }

    setWizardExecuting(true);
    setWizardStep(4);

    try {
      // "Esegui" after the step-3 preview = the explicit confirmation the server requires.
      const result = await linearService.bulkConfigure({ ...req, confirm: true });

      setWizardResult({
        imported: result.imported,
        xaccel_created: result.xaccel_created,
        xaccel_failed: result.xaccel_failed,
        results: result.results,
      });
      fetchData();
    } catch (error) {
      toast.error(errText(error) || t('linear.importError'));
      setWizardStep(3); // Go back to summary
    } finally {
      setWizardExecuting(false);
    }
  };

  // Wizard navigation
  const canGoNext = () => {
    switch (wizardStep) {
      case 1: return wizardSelected.size > 0;
      case 2: return wizardXaccelServer !== null && wizardResolutionIds.length > 0;
      case 3: return true;
      default: return false;
    }
  };

  const wizardNext = () => {
    if (wizardStep === 3) {
      executeWizard();
    } else if (wizardStep < 4) {
      setWizardStep(prev => prev + 1);
    }
  };

  const wizardBack = () => {
    if (wizardStep > 1 && !wizardExecuting) {
      setWizardStep(prev => prev - 1);
    }
  };

  // === EDIT MODAL ===
  const openEditModal = async (channel: LinearChannel) => {
    setEditingChannel(channel);
    setActiveTab('general');
    setCdnPreferredSearch('');
    setCdnBlacklistSearch('');
    const chServerIds = parseServerIds(channel.server_ids);
    setFormData({
      name: channel.name,
      dazn_asset_id: channel.dazn_asset_id,
      xaccel_channel_name: channel.xaccel_channel_name || '',
      xaccel_server_id: channel.xaccel_server_id?.toString() || '',
      server_ids: chServerIds.length > 0 ? chServerIds : (channel.xaccel_server_id ? [channel.xaccel_server_id] : []),
      xaccel_stream_enable: channel.xaccel_stream_enable === 1,
      cdn_blacklist: channel.cdn_blacklist ? JSON.parse(channel.cdn_blacklist) : [],
      cdn_preferred: channel.cdn_preferred ? JSON.parse(channel.cdn_preferred) : [],
      verify_age: channel.verify_age === 1,
      pin_protect: channel.pin_protect === 1,
      auto_restart_enabled: channel.auto_restart_enabled === 1,
      restart_times: channel.restart_times || '[]',
      video_map_overrides: channel.video_map_overrides ? JSON.parse(channel.video_map_overrides) : {},
      max_restart_attempts: channel.max_restart_attempts ?? 3,
      restart_cooldown_minutes: channel.restart_cooldown_minutes ?? 10,
    });
    setSelectedResolutions(channel.resolutions || []);
    setLegCount(0);
    // Load proxy assignments (per-server format)
    const editSids = chServerIds.length > 0 ? chServerIds : (channel.xaccel_server_id ? [channel.xaccel_server_id] : []);
    setProxyAssignments(parseProxyByResolution(channel.proxy_by_resolution, editSids[0] ?? null));
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingChannel(null);
    setProxyAssignments({});
  };

  const saveChannel = async () => {
    if (!editingChannel) return;
    setIsSaving(true);
    try {
      const vmOverrides = formData.video_map_overrides || {};
      const hasOverrides = Object.values(vmOverrides).some(v => v && v.trim() !== '');
      await linearService.update(editingChannel.id, {
        name: formData.name,
        xaccel_channel_name: formData.xaccel_channel_name || undefined,
        xaccel_server_id: formData.server_ids.length > 0 ? formData.server_ids[0] : (formData.xaccel_server_id ? parseInt(formData.xaccel_server_id) : undefined),
        server_ids: formData.server_ids,
        xaccel_stream_enable: formData.xaccel_stream_enable,
        cdn_blacklist: JSON.stringify(formData.cdn_blacklist || []),
        cdn_preferred: JSON.stringify(formData.cdn_preferred || []),
        verify_age: formData.verify_age,
        pin_protect: formData.pin_protect,
        auto_restart_enabled: formData.auto_restart_enabled,
        restart_times: formData.restart_times,
        video_map_overrides: hasOverrides ? JSON.stringify(vmOverrides) : null,
        max_restart_attempts: formData.max_restart_attempts,
        restart_cooldown_minutes: formData.restart_cooldown_minutes,
      });

      // Flush any pending resolution save
      clearTimeout(resolutionSaveTimer.current);
      await linearService.updateResolutions(editingChannel.id, selectedResolutions);

      toast.success(t('common.updated'));
      closeModal();
      fetchData();
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleProxyAssign = async (serverId: number, resolution: string, proxyId: number | null) => {
    if (!editingChannel) return;
    try {
      const result = await xaccelProxyService.assignToLinear(editingChannel.id, serverId, resolution, proxyId);
      if (result.success) {
        setProxyAssignments(prev => {
          const serverKey = String(serverId);
          return { ...prev, [serverKey]: { ...(prev[serverKey] || {}), [resolution]: proxyId } };
        });
        toast.success(t('proxies.saved'));
      }
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    }
  };

  // === TABLE ACTIONS ===
  // Restart SOLO su un server specifico (lascia gli altri leg attivi).
  const handleRestartServer = async (channelId: number, serverId: number, serverName: string) => {
    const key = `${channelId}-restart-${serverId}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      await linearService.restartWithKeys(channelId, serverId);
      toast.success(`${t('linear.channelRestarted')} (${serverName})`);
      fetchData();
      setTimeout(() => fetchData(), 3000);
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleAction = async (channelId: number, action: string) => {
    const key = `${channelId}-${action}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      switch (action) {
        case 'start':
          {
            const started = await linearService.start(channelId);
            toast.success(t('linear.channelStarted') + (started.skippedHeld ? t('linear.skippedHeldSuffix', { count: started.skippedHeld }) : ''));
          }
          break;
        case 'stop':
          await linearService.stop(channelId);
          toast.success(t('linear.channelStopped'));
          break;
        case 'restart':
          await linearService.restartWithKeys(channelId);
          toast.success(t('linear.channelRestarted'));
          break;
      }
      fetchData();
      // Re-check after 3s for Xaccel real status to propagate
      setTimeout(() => fetchData(), 3000);
    } catch (error) {
      toast.error(errText(error) || t('common.error'));
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  /**
   * Explicit way out when channels cannot be deleted on their servers: remove them from the CMS only, the streams
   * stay on the servers (listed in the preview). Never automatic.
   */
  const askRemoveFromCms = (items: Array<{ id: number; name: string; lines: string[] }>) => {
    setConfirmConfig({
      title: t('linear.confirm.removeFromCmsTitle', { count: items.length }),
      intro: t('linear.confirm.removeFromCmsIntro'),
      servers: [],
      names: items.map((i) => `${i.name}${i.lines.length ? ` (${i.lines.join('; ')})` : ''}`),
      confirmLabel: t('linear.confirm.removeChannelFromCms'),
      tone: 'danger',
      run: async () => {
        for (const it of items) {
          try {
            const r = await linearService.removeFromCms(it.id);
            toast.success(t('linear.removedFromCms', { name: it.name, count: r.legsLeft.length }));
          } catch (error) {
            toast.error(errText(error) || t('common.error'));
          }
        }
        fetchData();
      },
    });
  };

  const handleDelete = async (id: number) => {
    try {
      await linearService.delete(id);
      toast.success(t('common.deleted'));
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      setDeleteConfirm(null);
      if (getErrorCode(error) === 'LEGS_NOT_DELETED') {
        // Some streams could not be deleted on their servers: say so and offer the explicit CMS-only removal.
        toast.error(errText(error), { duration: 10000 });
        const ch = channels.find((c) => c.id === id);
        let lines: string[] = [];
        try {
          const { channels: legRows } = await linearService.listXaccelChannels(id);
          lines = legRows.map((l) => `${l.channel_name} @ ${xaccelServers.find((s) => s.id === l.xaccel_server_id)?.name || l.xaccel_server_id}`);
        } catch { /* preview without the leg list */ }
        askRemoveFromCms([{ id, name: ch?.name || String(id), lines }]);
        return;
      }
      toast.error(errText(error) || t('common.error'));
    }
  };

  // === BULK ACTIONS ===
  const handleBulk = async (action: string, confirmCount = 0) => {
    setBulkLoading(action);
    try {
      switch (action) {
        case 'start-all': {
          {
            const r = await linearService.startAll();
            toast.success(t('linear.allStarted') + (r.skippedHeld ? t('linear.skippedHeldSuffix', { count: r.skippedHeld }) : ''));
          }
          break;
        }
        case 'stop-all': {
          await linearService.stopAll();
          toast.success(t('linear.allStopped'));
          break;
        }
        case 'restart-all': {
          {
            const r = await linearService.restartAll(confirmCount);
            toast.success(t('linear.allRestarted') + (r.skippedHeld ? t('linear.skippedHeldSuffix', { count: r.skippedHeld }) : ''));
          }
          break;
        }
        case 'delete-all': {
          const r = await linearService.deleteAll();
          if (r.kept && r.kept.length > 0) {
            const kept = r.kept;
            toast.error(t('linear.deleteAllKept', { deleted: r.deleted, count: kept.length, names: kept.map((k) => k.name).join(', ') }), { duration: 10000 });
            askRemoveFromCms(kept.map((k) => ({ id: k.id, name: k.name, lines: k.errors })));
          } else {
            toast.success(t('linear.allDeleted'));
          }
          break;
        }
      }
      fetchData();
      // Re-check after 3s for Xaccel real status to propagate
      setTimeout(() => fetchData(), 3000);
    } catch (error) {
      // Restart All answers CONFIRM_COUNT_MISMATCH when channels started/stopped between preview and click: say so and
      // reopen the dialog with the fresh count.
      if (action === 'restart-all' && getErrorCode(error) === 'CONFIRM_COUNT_MISMATCH') {
        toast.error(t('linear.apiErrors.CONFIRM_COUNT_MISMATCH'));
        try {
          const fresh = await linearService.list();
          setChannels(fresh);
          askBulk('restart-all', fresh);
        } catch { /* the toast already says what to do */ }
        return;
      }
      toast.error(errText(error) || t('common.error'));
    } finally {
      setBulkLoading(null);
    }
  };

  /** Page Stop All / Restart All / Delete All: preview (servers, running vs stopped) and explicit confirm. */
  const askBulk = (action: 'start-all' | 'stop-all' | 'restart-all' | 'delete-all', list: LinearChannel[] = channels) => {
    if (bulkLoading) return;
    const p = pagePreview(list, action);
    if (p.channels === 0) { toast(t('linear.confirm.nothingToDo')); return; }
    const cfg = {
      'start-all': { title: 'linear.confirm.startAllTitle', intro: 'linear.confirm.startAllIntro', label: 'linear.confirm.start' },
      'stop-all': { title: 'linear.confirm.stopAllTitle', intro: 'linear.confirm.stopAllIntro', label: 'linear.confirm.stop' },
      'restart-all': { title: 'linear.confirm.restartAllTitle', intro: 'linear.confirm.restartAllIntro', label: 'linear.confirm.restart' },
      'delete-all': { title: 'linear.confirm.deleteAllTitle', intro: 'linear.confirm.deleteAllIntro', label: 'linear.confirm.delete' },
    }[action];
    setConfirmConfig({
      title: t(cfg.title, { count: p.channels }),
      intro: t(cfg.intro),
      servers: p.servers,
      names: p.names,
      confirmLabel: t(cfg.label),
      tone: action === 'start-all' ? 'primary' : 'danger',
      typedCount: action === 'restart-all' ? p.runningChannels : undefined,
      run: () => handleBulk(action, p.runningChannels),
    });
  };

  /** Row / badge / tab restart and row stop of ONE channel (optionally one server): preview and confirm. */
  const askChannelAction = (channel: LinearChannel, action: 'stop' | 'restart', server?: { id: number; name: string }, run?: () => Promise<void>) => {
    const servers = (channel.servers || [])
      .filter((s) => s.channels_total > 0 && (!server || s.id === server.id))
      .map((s) => ({ serverId: s.id, name: s.name, type: s.server_type, running: s.channels_running, stopped: Math.max(0, s.channels_total - s.channels_running) }));
    setConfirmConfig({
      title: server
        ? t('linear.confirm.restartServerTitle', { channel: channel.name, server: server.name })
        : t(action === 'stop' ? 'linear.confirm.stopChannelTitle' : 'linear.confirm.restartChannelTitle', { channel: channel.name }),
      intro: t(server ? 'linear.confirm.restartServerIntro' : action === 'stop' ? 'linear.confirm.stopChannelIntro' : 'linear.confirm.restartChannelIntro'),
      servers,
      confirmLabel: t(action === 'stop' ? 'linear.confirm.stop' : 'linear.confirm.restart'),
      tone: 'danger',
      run: run ?? (() => (server ? handleRestartServer(channel.id, server.id, server.name) : handleAction(channel.id, action))),
    });
  };

  // === RESTART TIMES HELPERS ===
  const restartTimesArray: string[] = (() => {
    try { return JSON.parse(formData.restart_times); } catch { return []; }
  })();

  const toggleRestartTime = (time: string) => {
    const arr = [...restartTimesArray];
    const idx = arr.indexOf(time);
    if (idx >= 0) arr.splice(idx, 1); else arr.push(time);
    arr.sort();
    setFormData(prev => ({ ...prev, restart_times: JSON.stringify(arr) }));
  };

  // Generate time slots every 30 minutes
  const timeSlots: string[] = [];
  for (let h = 0; h < 24; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'running': return 'badge-success';
      case 'stopped': return 'badge-neutral';
      case 'error': return 'badge-error';
      default: return 'badge-neutral';
    }
  };



  // === RENDER ===
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('linear.title')}</h1>
          <p className="text-dark-400 mt-1">
            {t('linear.channelsCount', { count: channels.length })}
            {runningCount > 0 && <span className="text-success-600 ml-2">{runningCount} {t('linear.running')}</span>}
            {stoppedCount > 0 && <span className="text-dark-500 ml-2">{stoppedCount} {t('linear.stopped')}</span>}
            {errorCount > 0 && <span className="text-error-600 ml-2">{errorCount} {t('linear.error')}</span>}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:gap-3">
          <button onClick={openWizard} className="btn-secondary">
            <Download size={18} />
            {t('linear.importDazn')}
          </button>
          <button onClick={() => {
            setEditingChannel(null);
            // The default server is only a pre-ticked suggestion: the user can untick it before saving.
            const suggested = xaccelServers.find((sv) => sv.is_default) || xaccelServers[0];
            setFormData({
              name: '', dazn_asset_id: '', xaccel_channel_name: '',
              xaccel_server_id: suggested ? String(suggested.id) : '', server_ids: suggested ? [suggested.id] : [], xaccel_stream_enable: false,
              cdn_blacklist: [], cdn_preferred: [],
              verify_age: false, pin_protect: false,
              auto_restart_enabled: false, restart_times: '[]',
              video_map_overrides: {},
              max_restart_attempts: 3, restart_cooldown_minutes: 10,
            });
            setSelectedResolutions([]);
            setActiveTab('general');
            setCdnPreferredSearch('');
            setCdnBlacklistSearch('');
            setShowModal(true);
          }} className="btn-primary">
            <Plus size={18} />
            {t('common.add')}
          </button>
        </div>
      </div>

      {/* Bulk actions */}
      {channels.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => askBulk('start-all')}
            disabled={bulkLoading !== null}
            className="btn-secondary text-sm"
          >
            {bulkLoading === 'start-all' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {t('linear.startAll')}
          </button>
          <button
            onClick={() => askBulk('stop-all')}
            disabled={bulkLoading !== null}
            className="btn-secondary text-sm"
          >
            {bulkLoading === 'stop-all' ? <Loader2 size={14} className="animate-spin" /> : <Square size={14} />}
            {t('linear.stopAll')}
          </button>
          <button
            onClick={() => askBulk('restart-all')}
            disabled={bulkLoading !== null}
            className="btn-secondary text-sm"
          >
            {bulkLoading === 'restart-all' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {t('linear.restartAll')}
          </button>
          <button
            onClick={() => askBulk('delete-all')}
            disabled={bulkLoading !== null}
            className="btn-secondary text-sm text-error-600 hover:bg-error-50"
          >
            {bulkLoading === 'delete-all' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
            {t('linear.deleteAll')}
          </button>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-800 rounded" />
            ))}
          </div>
        </div>
      ) : channels.length === 0 ? (
        <div className="card text-center py-12">
          <Tv size={48} className="mx-auto text-dark-500 mb-4" />
          <p className="text-dark-400 text-lg">{t('linear.noData')}</p>
          <p className="text-dark-500 text-sm mt-1">{t('linear.importHelp')}</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t('linear.channel')}</th>
                <th>{t('linear.server')}</th>
                <th>{t('linear.resolutions')}</th>
                <th>{t('linear.status')}</th>
                <th>{t('linear.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((channel) => (
                <tr key={channel.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="font-medium text-dark-100">{channel.name}</span>
                        <span title={t('linear.identityTitle')} className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${channel.playback_identity === 'androidtv' ? 'bg-violet-50 text-violet-700 border-violet-200' : 'bg-dark-800 text-dark-400 border-dark-700'}`}>
                          {channel.playback_identity === 'androidtv' ? t('linear.identityAndroidTv') : t('linear.identityWeb')}
                        </span>
                        {channel.xaccel_stream_enable === 1 && (
                          <CheckSquare size={14} className="inline ml-1.5 text-success-600" />
                        )}
                        {channel.held_legs && channel.held_legs.length > 0 && (
                          <span
                            className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border bg-amber-50 text-amber-700 border-amber-200"
                            title={`${t('linear.listHeldTitle')}\n${channel.held_legs.map((h) => `${h.name} @ ${channel.servers?.find((s) => s.id === h.serverId)?.name ?? h.serverId}: ${h.reason === 'user-stop' ? t('linear.listHeldUserStop') : t('linear.listHeldUnwanted')}`).join('\n')}`}
                          >
                            {t('linear.listHeldBadge', { count: channel.held_legs.length })}
                          </span>
                        )}
                        {channel.subtitle && (
                          <span className="block text-xs text-dark-500">{channel.subtitle}</span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    {(channel.servers && channel.servers.length > 0) ? (
                      <div className="flex flex-wrap gap-1">
                        {channel.servers.map(srv => (
                          <span
                            key={srv.id}
                            className={`inline-flex items-center gap-1 text-xs px-1.5 py-0.5 rounded ${
                              srv.server_type === 'ffstream'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full ${srv.running ? 'bg-green-500' : 'bg-gray-300'}`} />
                            {srv.name}
                            {srv.channels_total > 0 && (
                              <span
                                className="opacity-70 font-mono"
                                title={t('linear.listLegCountsTitle', { running: srv.channels_running, total: srv.channels_total, held: srv.channels_held ?? 0 }) + (srv.unchecked ? ` ${t('linear.listLegCountsUnchecked')}` : '')}
                              >
                                {srv.channels_running}/{srv.channels_total}{srv.unchecked ? '?' : ''}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-dark-500 text-sm">{channel.xaccel_name || '-'}</span>
                    )}
                  </td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {(channel.resolutions || []).length > 0 ? (
                        resolutions
                          .filter(r => channel.resolutions?.includes(r.id))
                          .map(r => (
                            <span key={r.id} className="badge-primary text-xs">{r.name}</span>
                          ))
                      ) : (
                        <span className="text-dark-500 text-xs">-</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <span className={getStatusBadge(channel.status)}>
                        {channel.status}
                      </span>
                      {channel.status === 'running' && channel.servers && channel.servers.length > 0 ? (
                        channel.servers.filter(srv => srv.channels_total > 0).map(srv => (
                          <button
                            key={srv.id}
                            onClick={() => askChannelAction(channel, 'restart', { id: srv.id, name: srv.name })}
                            disabled={!!actionLoading[`${channel.id}-restart-${srv.id}`]}
                            className={`text-[10px] px-1.5 py-0.5 rounded font-medium inline-flex items-center gap-0.5 hover:opacity-75 disabled:opacity-50 ${
                              srv.running
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-600'
                            }`}
                            title={t('linear.restartServerBadgeTitle', { server: srv.name, running: srv.channels_running, total: srv.channels_total })}
                          >
                            {serverTypeShort(srv.server_type)} {srv.running ? '\u2713' : '\u2717'}
                            <span className="opacity-60">{actionLoading[`${channel.id}-restart-${srv.id}`] ? '\u22ef' : '\u21bb'}</span>
                          </button>
                        ))
                      ) : channel.status === 'running' && channel.real_status === 'running' ? (
                        <span className="badge-success text-xs">{serverTypeShort(channel.server_type)} {'\u2713'}</span>
                      ) : null}
                      {channel.mismatch && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-yellow-50 text-yellow-700 border border-yellow-200">!</span>
                      )}
                      {channel.live_while_error && (
                        <span title={t('linear.liveWhileErrorTitle')} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-green-50 text-green-700 border border-green-200">
                          {t('linear.liveWhileError')}
                        </span>
                      )}
                    </div>
                    {channel.status_message && (
                      <p className="text-xs text-dark-500 mt-1 max-w-[200px] truncate" title={statusMessageText(channel.status_message, t)}>
                        {statusMessageText(channel.status_message, t)}
                      </p>
                    )}
                    {(channel.health_restart_count ?? 0) > 0 && channel.status !== 'error' && (
                      <span className="text-xs text-warning-600 font-mono">
                        retry {channel.health_restart_count}/{channel.max_restart_attempts ?? 3}
                      </span>
                    )}
                  </td>
                  <td>
                    {deleteConfirm === channel.id ? (
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleDelete(channel.id)} className="p-1.5 rounded-lg bg-error-600 text-white hover:bg-error-500">
                          <Check size={16} />
                        </button>
                        <button onClick={() => setDeleteConfirm(null)} className="p-1.5 rounded-lg hover:bg-dark-800 text-dark-400">
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        {channel.status !== 'running' ? (
                          channel.xaccel_stream_enable === 1 ? (
                            <button
                              onClick={() => handleAction(channel.id, 'start')}
                              disabled={!!actionLoading[`${channel.id}-start`]}
                              className="p-1.5 rounded-lg hover:bg-success-50 text-dark-400 hover:text-success-600"
                              title={t('linear.start')}
                            >
                              {actionLoading[`${channel.id}-start`] ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} />}
                            </button>
                          ) : (
                            <span className="p-1.5 text-dark-500" title={t('linear.notEnabled')}>
                              <Play size={16} className="opacity-30" />
                            </span>
                          )
                        ) : (
                          <>
                            <button
                              onClick={() => askChannelAction(channel, 'stop')}
                              disabled={!!actionLoading[`${channel.id}-stop`]}
                              className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                              title={t('linear.stop')}
                            >
                              {actionLoading[`${channel.id}-stop`] ? <Loader2 size={16} className="animate-spin" /> : <Square size={16} />}
                            </button>
                            <button
                              onClick={() => askChannelAction(channel, 'restart')}
                              disabled={!!actionLoading[`${channel.id}-restart`]}
                              className="p-1.5 rounded-lg hover:bg-primary-50 text-dark-400 hover:text-primary-600"
                              title={t('linear.restart')}
                            >
                              {actionLoading[`${channel.id}-restart`] ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => openEditModal(channel)}
                          className="p-1.5 rounded-lg hover:bg-dark-800 text-dark-400 hover:text-dark-100"
                          title={t('common.edit')}
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(channel.id)}
                          className="p-1.5 rounded-lg hover:bg-error-50 text-dark-400 hover:text-error-600"
                          title={t('common.delete')}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ============= WIZARD MODAL (4-step bulk configure) ============= */}
      {showWizard && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeWizard}
        >
          <div className="card w-full max-w-2xl animate-fade-in flex flex-col max-h-[calc(100vh-2rem)] !p-0" onClick={(e) => e.stopPropagation()}>
            {/* Header with step indicator */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-dark-700">
              <div>
                <h2 className="text-xl font-semibold text-dark-100">{t('linear.bulkWizardTitle')}</h2>
                <p className="text-sm text-dark-500 mt-1">
                  {t('linear.step')} {wizardStep}/4: {
                    wizardStep === 1 ? t('linear.stepSelectChannels') :
                    wizardStep === 2 ? t('linear.stepConfiguration') :
                    wizardStep === 3 ? t('linear.stepSummary') :
                    t('linear.stepExecution')
                  }
                </p>
              </div>
              {!wizardExecuting && (
                <button onClick={closeWizard} className="p-1.5 rounded-lg hover:bg-dark-800 text-dark-400">
                  <X size={20} />
                </button>
              )}
            </div>

            {/* Step progress bar */}
            <div className="flex px-6 py-3 gap-2 flex-shrink-0">
              {[1, 2, 3, 4].map((step) => (
                <div
                  key={step}
                  className={`flex-1 h-1.5 rounded-full transition-colors ${
                    step <= wizardStep ? 'bg-primary-500' : 'bg-dark-700'
                  }`}
                />
              ))}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 pb-4">
              {wizardLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 size={32} className="animate-spin text-primary-600" />
                </div>
              ) : (
                <>
                  {/* STEP 1: Select channels */}
                  {wizardStep === 1 && (
                    <>
                      {wizardChannels.length === 0 ? (
                        <p className="text-dark-400 text-center py-12">{t('linear.noChannelsAvailable')}</p>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 mb-4">
                            <button onClick={selectAllWizard} className="text-sm text-primary-600 hover:text-primary-700">
                              {t('common.selectAll')}
                            </button>
                            <span className="text-dark-600">|</span>
                            <button onClick={deselectAllWizard} className="text-sm text-dark-400 hover:text-dark-300">
                              {t('common.deselectAll')}
                            </button>
                            <span className="ml-auto text-sm text-dark-500">
                              {t('linear.selected', { count: wizardSelected.size })}
                            </span>
                          </div>
                          <div className="space-y-2">
                            {wizardChannels.map((ch) => {
                              const isSelected = wizardSelected.has(ch.id);
                              const isImported = ch.already_imported;
                              return (
                                <label
                                  key={ch.id}
                                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-all ${
                                    isImported ? 'border-dark-700 bg-dark-800 opacity-60' :
                                    isSelected ? 'border-primary-500 bg-primary-50' :
                                    'border-dark-700 hover:border-dark-600'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isImported}
                                    onChange={() => toggleWizardChannel(ch.id)}
                                    className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className={`text-sm font-medium ${isSelected && !isImported ? 'text-primary-600' : 'text-dark-200'}`}>
                                      {ch.title}
                                    </span>
                                    {ch.subtitle && <span className="block text-xs text-dark-500">{ch.subtitle}</span>}
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {ch.is_live && <span className="badge-success text-xs">Live</span>}
                                    {isImported && <span className="badge-neutral text-xs">{t('linear.imported')}</span>}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {/* STEP 2: Configuration */}
                  {wizardStep === 2 && (
                    <div className="space-y-6">
                      {/* Streaming Servers */}
                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">{t('streaming.servers')} *</label>
                        {xaccelServers.length === 0 ? (
                          <p className="text-sm text-error-500">{t('linear.noServersConfigured')}</p>
                        ) : (
                          <div className="space-y-1.5 bg-dark-800 rounded-lg p-3 border border-dark-700">
                            {xaccelServers.map((server) => {
                              const checked = wizardServerIds.includes(server.id);
                              return (
                                <label key={server.id} className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-dark-700 transition-colors">
                                  <input
                                    type="checkbox"
                                    className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                                    checked={checked}
                                    onChange={() => {
                                      const next = checked
                                        ? wizardServerIds.filter(id => id !== server.id)
                                        : [...wizardServerIds, server.id];
                                      setWizardServerIds(next);
                                      setWizardXaccelServer(next.length > 0 ? next[0] : null);
                                    }}
                                  />
                                  <span className="text-sm">{server.name}</span>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-dark-300">
                                    {serverTypeLabel((server as any).server_type)}
                                  </span>
                                  {server.is_default === 1 && (
                                    <span className="text-[10px] text-primary-400">({t('common.default')})</span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Resolutions */}
                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">{t('linear.resolutions')} *</label>
                        {resolutions.length === 0 ? (
                          <p className="text-sm text-error-500">{t('linear.noResolutionsConfigured')}</p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {resolutions.map((r) => (
                              <label
                                key={r.id}
                                className={`px-3 py-1.5 rounded-md border cursor-pointer transition-all text-sm ${
                                  wizardResolutionIds.includes(r.id)
                                    ? 'border-primary-500 bg-primary-50 text-primary-600'
                                    : 'border-dark-600 hover:border-dark-500 text-dark-400'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={wizardResolutionIds.includes(r.id)}
                                  onChange={() => toggleWizardResolution(r.id)}
                                  className="sr-only"
                                />
                                {r.name}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Naming */}
                      <div className="border-t border-dark-700 pt-4">
                        <label className="block text-sm font-medium text-dark-300 mb-2">{t('linear.prefixLabel')}</label>
                        <input
                          type="text"
                          value={wizardPrefix}
                          onChange={(e) => setWizardPrefix(e.target.value)}
                          placeholder={t('linear.prefixPlaceholder')}
                          className="input w-full text-sm"
                        />
                        <p className="text-xs text-dark-500 mt-1">{t('linear.prefixHint')}</p>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-dark-300 mb-2">{t('linear.separator')}</label>
                        <div className="flex gap-2">
                          {[
                            { value: '_' as const, label: t('linear.separatorUnderscore') },
                            { value: ' ' as const, label: t('linear.separatorSpace') },
                            { value: '-' as const, label: t('linear.separatorDash') },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              onClick={() => setWizardSeparator(opt.value)}
                              className={`px-3 py-1.5 rounded-md border text-sm transition-all ${
                                wizardSeparator === opt.value
                                  ? 'border-primary-500 bg-primary-50 text-primary-600'
                                  : 'border-dark-600 hover:border-dark-500 text-dark-400'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Preview */}
                      {wizardResolutionIds.length > 0 && (
                        <div className="bg-dark-800 rounded-md p-3">
                          <p className="text-xs text-dark-500 mb-1">{t('linear.namePreview')}:</p>
                          <p className="font-mono text-sm text-primary-400">
                            {buildPreviewName('Eurosport 1', resolutions.find(r => r.id === wizardResolutionIds[0])?.name || 'HD')}
                          </p>
                        </div>
                      )}

                      {/* Proxy per resolution - per server */}
                      {wizardServerIds.length > 0 && wizardResolutionIds.length > 0 && (() => {
                        const hasAnyProxies = wizardServerIds.some(sId => xaccelProxies.some(p => p.xaccelServerId === sId));
                        if (!hasAnyProxies) return null;
                        return (
                          <div className="border-t border-dark-700 pt-4">
                            <label className="block text-sm font-medium text-dark-300 mb-2">{t('linear.proxyByResolution')}</label>
                            <div className="space-y-3">
                              {wizardServerIds.filter((sId) => (xaccelServers.find((srv) => srv.id === sId) as any)?.server_type !== 'ssp').map((sId) => {
                                const server = xaccelServers.find(s => s.id === sId);
                                if (!server) return null;
                                const sProxies = xaccelProxies.filter(p => p.xaccelServerId === sId);
                                if (sProxies.length === 0) return null;
                                return (
                                  <div key={sId} className="rounded-lg border border-dark-700 p-3">
                                    <div className="text-xs font-medium text-dark-400 mb-2 flex items-center gap-2">
                                      {server.name}
                                      <span className="px-1.5 py-0.5 rounded bg-dark-600 text-dark-300">
                                        {serverTypeLabel((server as any).server_type)}
                                      </span>
                                    </div>
                                    <div className="space-y-1.5">
                                      {wizardResolutionIds.map((resId) => {
                                        const res = resolutions.find(r => r.id === resId);
                                        if (!res) return null;
                                        const serverKey = String(sId);
                                        const currentVal = wizardProxyByRes[serverKey]?.[res.name] ?? null;
                                        return (
                                          <div key={resId} className="flex items-center gap-3">
                                            <span className="w-16 text-sm font-medium text-dark-400">{res.name}:</span>
                                            <select
                                              value={currentVal || ''}
                                              onChange={(e) => setWizardProxyByRes(prev => {
                                                const sk = String(sId);
                                                return { ...prev, [sk]: { ...(prev[sk] || {}), [res.name]: e.target.value ? parseInt(e.target.value) : null } };
                                              })}
                                              className="input flex-1 text-sm"
                                            >
                                              <option value="">{t('linear.noProxy')}</option>
                                              {sProxies.map((p) => (
                                                <option key={p.id} value={p.id}>{p.name}</option>
                                              ))}
                                            </select>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Auto-restart */}
                      <div className="border-t border-dark-700 pt-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={wizardAutoRestart}
                            onChange={(e) => setWizardAutoRestart(e.target.checked)}
                            className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm font-medium text-dark-300">{t('linear.autoRestartConfig')}</span>
                        </label>
                        {wizardAutoRestart && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {wizardRestartTimes.map((time, idx) => (
                              <div key={idx} className="flex items-center gap-1 bg-dark-800 px-2 py-1 rounded">
                                <input
                                  type="time"
                                  value={time}
                                  onChange={(e) => updateWizardRestartTime(idx, e.target.value)}
                                  className="bg-transparent border-none text-sm text-dark-200 w-20"
                                />
                                <button onClick={() => removeWizardRestartTime(idx)} className="text-dark-500 hover:text-error-500">
                                  <X size={14} />
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={addWizardRestartTime}
                              className="px-2 py-1 text-sm text-primary-600 hover:bg-primary-50 rounded"
                            >
                              + {t('linear.addRestartTime')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Summary */}
                  {wizardStep === 3 && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-dark-500">{t('linear.summaryChannels')}:</span>
                          <span className="ml-2 font-medium text-dark-200">{wizardSelected.size}</span>
                        </div>
                        <div>
                          <span className="text-dark-500">{t('streaming.servers')}:</span>
                          <span className="ml-2 font-medium text-dark-200">
                            {wizardServerIds.map(id => xaccelServers.find(s => s.id === id)?.name).filter(Boolean).join(', ') || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">{t('linear.summaryResolutions')}:</span>
                          <span className="ml-2 font-medium text-dark-200">
                            {wizardResolutionIds.map(id => resolutions.find(r => r.id === id)?.name).filter(Boolean).join(', ')}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">{t('linear.summaryXaccelTotal')}:</span>
                          <span className="ml-2 font-medium text-primary-500">
                            {wizardSelected.size * wizardResolutionIds.length}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">{t('linear.summaryNaming')}:</span>
                          <span className="ml-2 font-mono text-xs text-dark-300">
                            {wizardPrefix ? `${wizardPrefix}${wizardSeparator}` : ''}{'{nome}'}{wizardSeparator}{'{res}'}
                          </span>
                        </div>
                        <div>
                          <span className="text-dark-500">{t('linear.summaryAutoRestart')}:</span>
                          <span className={`ml-2 font-medium ${wizardAutoRestart ? 'text-success-500' : 'text-dark-400'}`}>
                            {wizardAutoRestart ? `${t('linear.activeLabel')} (${wizardRestartTimes.length})` : t('linear.inactiveLabel')}
                          </span>
                        </div>
                      </div>

                      {/* Create Xaccel channels option */}
                      <div className="border-t border-dark-700 pt-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={wizardCreateXaccel}
                            onChange={(e) => setWizardCreateXaccel(e.target.checked)}
                            className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm text-dark-200">{t('linear.createXaccelChannels')}</span>
                        </label>
                        <p className="text-xs text-dark-500 mt-1 ml-7">{t('linear.createXaccelChannelsHint')}</p>
                      </div>

                      {/* Real state of the selected servers (dry run): nothing is created before "Esegui" */}
                      {wizardCreateXaccel && (
                        <div className="border-t border-dark-700 pt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <p className="text-sm font-medium text-dark-300">{t('linear.wizardPlanTitle')}</p>
                            <button type="button" className="ml-auto text-xs text-dark-300 underline disabled:opacity-50" disabled={wizardPlanLoading} onClick={loadWizardPlan}>{t('linear.wizardPlanRefresh')}</button>
                          </div>
                          {wizardPlanLoading ? (
                            <p className="text-xs text-dark-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> {t('linear.wizardPlanReading')}</p>
                          ) : wizardPlan && (
                            <div className="space-y-1">
                              {wizardPlan.map((p) => {
                                const n = (v: string) => p.legs.filter((l) => l.verdict === v).length;
                                return (
                                  <div key={p.serverId} className="text-xs rounded border border-dark-700 px-2 py-1">
                                    <span className="font-medium text-dark-200">{p.serverName}</span>{' '}
                                    {p.reachable ? (
                                      <span className="text-dark-400">
                                        {t('linear.wizardPlanCreate', { count: n('create') })}
                                        {n('exists-on-server') > 0 && ` · ${t('linear.wizardPlanExists', { count: n('exists-on-server') })}`}
                                        {n('conflict') > 0 && ` · ${t('linear.wizardPlanConflict', { count: n('conflict') })}`}
                                        {n('unknown') > 0 && ` · ${t('linear.wizardPlanUnknown', { count: n('unknown') })}`}
                                      </span>
                                    ) : (
                                      <span className="text-error-600">{t('linear.wizardPlanUnreachable', { error: p.error || '?' })}</span>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Preview channels */}
                      <div className="border-t border-dark-700 pt-4">
                        <p className="text-sm font-medium text-dark-300 mb-2">{t('linear.previewChannels')}:</p>
                        <div className="bg-dark-800 rounded-md p-3 max-h-48 overflow-y-auto">
                          <div className="space-y-1 font-mono text-xs">
                            {[...wizardSelected].slice(0, 3).flatMap((id) => {
                              const ch = wizardChannels.find(c => c.id === id);
                              if (!ch) return [];
                              return wizardResolutionIds.map((resId) => {
                                const res = resolutions.find(r => r.id === resId);
                                return (
                                  <div key={`${id}-${resId}`} className="text-dark-400">
                                    {buildPreviewName(ch.title, res?.name || '')}
                                  </div>
                                );
                              });
                            })}
                            {wizardSelected.size > 3 && (
                              <div className="text-dark-500 pt-1">
                                ... {t('linear.channelsToCreate', { count: (wizardSelected.size - 3) * wizardResolutionIds.length })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: Execution */}
                  {wizardStep === 4 && (
                    <div className="py-8">
                      {wizardExecuting ? (
                        <div className="text-center">
                          <Loader2 size={48} className="animate-spin text-primary-600 mx-auto mb-4" />
                          <p className="text-dark-300">{t('linear.executing')}</p>
                          <p className="text-sm text-dark-500 mt-2">{t('linear.creatingXaccel')}</p>
                        </div>
                      ) : wizardResult ? (
                        <div className="text-center">
                          <div className="w-16 h-16 rounded-full bg-success-100 flex items-center justify-center mx-auto mb-4">
                            <Check size={32} className="text-success-600" />
                          </div>
                          <h3 className="text-xl font-semibold text-dark-100 mb-4">{t('linear.bulkCompleted')}</h3>
                          <div className="space-y-2 text-sm">
                            <p className="text-dark-300">{t('linear.bulkImported', { count: wizardResult.imported })}</p>
                            <p className="text-success-500">{t('linear.bulkXaccelCreated', { count: wizardResult.xaccel_created })}</p>
                            {wizardResult.xaccel_failed > 0 && (
                              <p className="text-error-500">{t('linear.bulkXaccelFailed', { count: wizardResult.xaccel_failed })}</p>
                            )}
                          </div>
                          {wizardResult.results && wizardResult.results.length > 0 && (
                            <div className="mt-4 space-y-1 text-left max-h-56 overflow-y-auto">
                              {wizardResult.results.map((r) => (
                                <div key={r.serverId} className="text-xs rounded border border-dark-700 px-2 py-1">
                                  <span className="font-medium text-dark-200">{r.serverName}</span>{' '}
                                  <span className="text-dark-400">{t('linear.wizardResultLine', { created: r.created, skipped: r.skipped.length, failed: r.failed.length })}</span>
                                  {r.failed.map((f) => <p key={`${f.channelId}-${f.name}`} className="text-error-600 break-words"><span className="font-mono">{f.name || f.channel}</span>: {f.error}</p>)}
                                  {r.skipped.filter((sk) => sk.reason === 'unreachable' || sk.reason === 'unknown-state').length > 0 && (
                                    <p className="text-warning-600">{t('linear.wizardResultUnreachable', { count: r.skipped.filter((sk) => sk.reason === 'unreachable' || sk.reason === 'unknown-state').length })}</p>
                                  )}
                                </div>
                              ))}
                              {bulkRetryChannels(wizardResult.results).length > 0 && (
                                <button type="button" className="btn-secondary text-sm mt-2" disabled={wizardRetrying} onClick={retryWizardFailed}>
                                  {wizardRetrying && <Loader2 size={14} className="animate-spin" />} {t('linear.wizardRetryFailed')}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-dark-700 flex-shrink-0">
              <div>
                {wizardStep === 1 && wizardChannels.filter(ch => ch.already_imported).length > 0 && (
                  <p className="text-sm text-dark-500">
                    {t('linear.alreadyImported', { count: wizardChannels.filter(ch => ch.already_imported).length })}
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                {wizardStep === 4 && !wizardExecuting ? (
                  <button onClick={closeWizard} className="btn-primary">
                    {t('linear.close')}
                  </button>
                ) : (
                  <>
                    {wizardStep > 1 && !wizardExecuting && (
                      <button onClick={wizardBack} className="btn-secondary">
                        {t('common.back')}
                      </button>
                    )}
                    {wizardStep < 4 && (
                      <button
                        onClick={wizardNext}
                        disabled={!canGoNext() || wizardExecuting}
                        className="btn-primary"
                      >
                        {wizardStep === 3 ? t('linear.execute') : t('common.next')}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============= EDIT MODAL ============= */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="card w-full max-w-6xl animate-fade-in flex flex-col max-h-[calc(100vh-2rem)] !p-0" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
              <h2 className="text-xl font-semibold text-dark-100">
                {editingChannel ? editingChannel.name : t('linear.newTitle')}
              </h2>
              <button onClick={closeModal} className="p-1.5 rounded-lg hover:bg-dark-800 text-dark-400">
                <X size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-4 md:px-6 border-b border-dark-700 flex-shrink-0 overflow-x-auto scrollbar-hide">
              {([
                { id: 'general' as const, label: t('linear.tabGeneral'), icon: Settings },
                { id: 'streaming' as const, label: t('linear.tabStreaming'), icon: Tv },
                { id: 'restart' as const, label: t('linear.tabRestart'), icon: Clock },
                { id: 'xaccel' as const, label: t('linear.tabXaccelChannels'), icon: Server, disabled: !editingChannel },
              ]).map((tab) => (
                <button
                  key={tab.id}
                  onClick={async () => {
                    // Auto-save form before switching to Xaccel tab
                    if (tab.id === 'xaccel' && editingChannel) {
                      try {
                        clearTimeout(resolutionSaveTimer.current);
                        const vmOverridesAutoSave = formData.video_map_overrides || {};
                        const hasOverridesAutoSave = Object.values(vmOverridesAutoSave).some(v => v && v.trim() !== '');
                        // Bounded: removing a deselected server's streams can take long on a dead server; the save
                        // goes on on the server side, the tab must not wait for it forever.
                        await Promise.all([
                          linearService.update(editingChannel.id, {
                            name: formData.name,
                            xaccel_channel_name: formData.xaccel_channel_name || undefined,
                            xaccel_server_id: formData.server_ids.length > 0 ? formData.server_ids[0] : (formData.xaccel_server_id ? parseInt(formData.xaccel_server_id) : undefined),
                            server_ids: formData.server_ids,
                            xaccel_stream_enable: formData.xaccel_stream_enable,
                            cdn_blacklist: JSON.stringify(formData.cdn_blacklist || []),
                            cdn_preferred: JSON.stringify(formData.cdn_preferred || []),
                            verify_age: formData.verify_age,
                            pin_protect: formData.pin_protect,
                            auto_restart_enabled: formData.auto_restart_enabled,
                            restart_times: formData.restart_times,
                            video_map_overrides: hasOverridesAutoSave ? JSON.stringify(vmOverridesAutoSave) : null,
                            max_restart_attempts: formData.max_restart_attempts,
                            restart_cooldown_minutes: formData.restart_cooldown_minutes,
                          }, { timeoutMs: XACCEL_TAB_REQUEST_TIMEOUT_MS }),
                          linearService.updateResolutions(editingChannel.id, selectedResolutions),
                        ]);
                      } catch {
                        toast.error(t('linear.autoSaveError'));
                      }
                      bumpLegState();
                    }
                    setActiveTab(tab.id);
                  }}
                  disabled={tab.disabled}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-dark-800 text-dark-100 border-b-2 border-primary-500'
                      : 'text-dark-400 hover:text-dark-200 hover:bg-dark-800'
                  } ${tab.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <tab.icon size={16} />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {/* TAB: GENERALE */}
              {activeTab === 'general' && (
                <div className="space-y-4">
                  <div>
                    <label className="label">{t('linear.nameLabel')}</label>
                    <input
                      type="text"
                      className="input"
                      value={formData.name}
                      onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="DAZN 1"
                    />
                  </div>

                  <div>
                    <label className="label">{t('linear.daznAssetId')}</label>
                    <input
                      type="text"
                      className="input bg-dark-800"
                      value={formData.dazn_asset_id}
                      onChange={e => setFormData(prev => ({ ...prev, dazn_asset_id: e.target.value }))}
                      readOnly={!!editingChannel}
                      placeholder="dazn-1-it"
                    />
                  </div>

                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.verify_age}
                        onChange={e => setFormData(prev => ({ ...prev, verify_age: e.target.checked }))}
                        className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-dark-200">{t('linear.verifyAge')}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.pin_protect}
                        onChange={e => setFormData(prev => ({ ...prev, pin_protect: e.target.checked }))}
                        className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-dark-200">{t('linear.pinProtect')}</span>
                    </label>
                  </div>
                </div>
              )}

              {/* TAB: STREAMING */}
              {activeTab === 'streaming' && (
                <div className="space-y-5">
                  <div>
                    <label className="label">
                      {t('linear.xaccelChannelName')} <HintTooltip text={t('linear.xaccelChannelNameHint')} />
                    </label>
                    <input
                      type="text"
                      className="input font-mono"
                      value={formData.xaccel_channel_name}
                      onChange={e => setFormData(prev => ({ ...prev, xaccel_channel_name: e.target.value }))}
                      placeholder={formData.name.replace(/[^a-zA-Z0-9\s_-]/g, '').replace(/\s+/g, '_').substring(0, 80) || 'Channel_Name'}
                    />
                  </div>

                  <div>
                    <label className="label">
                      {t('streaming.servers')} <HintTooltip text={t('linear.xaccelServerHint')} />
                    </label>
                    <div className="space-y-1.5 bg-dark-800 rounded-lg p-3 border border-dark-700">
                      {xaccelServers.length === 0 ? (
                        <p className="text-xs text-dark-500">{t('streaming.noServersSelected')}</p>
                      ) : (
                        xaccelServers.map((server) => {
                          const checked = formData.server_ids.includes(server.id);
                          return (
                            <label key={server.id} className="flex items-center gap-2 cursor-pointer py-1 px-1 rounded hover:bg-dark-700 transition-colors">
                              <input
                                type="checkbox"
                                className="rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                                checked={checked}
                                onChange={() => {
                                  setFormData(prev => {
                                    const next = checked
                                      ? prev.server_ids.filter(id => id !== server.id)
                                      : [...prev.server_ids, server.id];
                                    return { ...prev, server_ids: next, xaccel_server_id: next.length > 0 ? next[0].toString() : '' };
                                  });
                                }}
                              />
                              <span className="text-sm">{server.name}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded bg-dark-600 text-dark-300">
                                {serverTypeLabel((server as any).server_type)}
                              </span>
                              {server.is_default === 1 && (
                                <span className="text-[10px] text-primary-400">(Default)</span>
                              )}
                            </label>
                          );
                        })
                      )}
                    </div>
                  </div>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.xaccel_stream_enable}
                      onChange={e => setFormData(prev => ({ ...prev, xaccel_stream_enable: e.target.checked }))}
                      className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-dark-200">
                      {t('linear.streamEnable')} <HintTooltip text={t('linear.streamEnableHint')} />
                    </span>
                  </label>

                  {/* CDN Selection */}
                  {cdnNames.length > 0 && (
                    <>
                      {/* CDN Preferred multi-select */}
                      <div>
                        <label className="label flex items-center gap-1">
                          <Globe size={14} />
                          {t('linear.cdnPreferred')} <HintTooltip text={t('linear.cdnPreferredHint')} />
                        </label>
                        <div className="relative mb-1.5">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-dark-500" />
                          <input
                            type="text"
                            value={cdnPreferredSearch}
                            onChange={(e) => setCdnPreferredSearch(e.target.value)}
                            placeholder={t('common.search')}
                            className="input text-xs py-1 pl-7"
                          />
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto border border-dark-700 rounded-md p-2">
                          {cdnNames
                            .filter(c => !c.is_blacklisted)
                            .filter(c => c.cdn_name.toLowerCase().includes(cdnPreferredSearch.trim().toLowerCase()))
                            .map((cdn) => {
                            const isSelected = (formData.cdn_preferred || []).includes(cdn.cdn_name);
                            return (
                              <label
                                key={cdn.id}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-all ${
                                  isSelected ? 'bg-primary-50 text-primary-600' : 'hover:bg-dark-800 text-dark-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      cdn_preferred: isSelected
                                        ? prev.cdn_preferred.filter(n => n !== cdn.cdn_name)
                                        : [...prev.cdn_preferred, cdn.cdn_name],
                                    }));
                                  }}
                                  className="w-3.5 h-3.5 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="font-mono">{cdn.cdn_name}</span>
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      {/* CDN Blacklist multi-select */}
                      <div>
                        <label className="label flex items-center gap-1">
                          <Globe size={14} />
                          {t('linear.cdnBlacklist')} <HintTooltip text={t('linear.cdnBlacklistHint')} />
                        </label>
                        <div className="relative mb-1.5">
                          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-dark-500" />
                          <input
                            type="text"
                            value={cdnBlacklistSearch}
                            onChange={(e) => setCdnBlacklistSearch(e.target.value)}
                            placeholder={t('common.search')}
                            className="input text-xs py-1 pl-7"
                          />
                        </div>
                        <div className="space-y-1 max-h-32 overflow-y-auto border border-dark-700 rounded-md p-2">
                          {cdnNames
                            .filter(c => c.cdn_name.toLowerCase().includes(cdnBlacklistSearch.trim().toLowerCase()))
                            .map((cdn) => {
                            const isSelected = (formData.cdn_blacklist || []).includes(cdn.cdn_name);
                            return (
                              <label
                                key={cdn.id}
                                className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-xs transition-all ${
                                  isSelected ? 'bg-error-50 text-error-600' : 'hover:bg-dark-800 text-dark-300'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => {
                                    setFormData(prev => ({
                                      ...prev,
                                      cdn_blacklist: isSelected
                                        ? prev.cdn_blacklist.filter(n => n !== cdn.cdn_name)
                                        : [...prev.cdn_blacklist, cdn.cdn_name],
                                    }));
                                  }}
                                  className="w-3.5 h-3.5 rounded border-dark-600 text-error-600 focus:ring-error-500"
                                />
                                <span className="font-mono">{cdn.cdn_name}</span>
                                {cdn.is_blacklisted && (
                                  <span className="text-error-500 text-[10px] ml-auto">{t('linear.globalBL')}</span>
                                )}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  <div>
                    <label className="label">
                      {t('linear.resolutionsLabel')} <HintTooltip text={t('linear.resolutionsHint')} />
                    </label>
                    {resolutions.length === 0 ? (
                      <p className="text-dark-500 text-sm">{t('linear.noResolutions')}</p>
                    ) : (
                      <div className="space-y-2">
                        {resolutions.map((res) => {
                          const isSelected = selectedResolutions.includes(res.id);
                          return (
                            <label
                              key={res.id}
                              className={`flex items-center gap-3 px-3 py-2.5 rounded-md border cursor-pointer transition-all ${
                                isSelected
                                  ? 'border-primary-500 bg-primary-50'
                                  : 'border-dark-700 hover:border-dark-600'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleResolution(res.id)}
                                className="w-4 h-4 rounded border-dark-600 text-primary-600 focus:ring-primary-500"
                              />
                              <span className={`text-sm font-medium ${isSelected ? 'text-primary-600' : 'text-dark-200'}`}>
                                {res.name}
                              </span>
                              <span className="text-xs text-dark-500 font-mono">{res.xaccel_value}</span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* Video Map Override */}
                  {selectedResolutions.length > 0 && (
                    <div>
                      <label className="label">
                        {t('linear.videoMapOverride')} <HintTooltip text={t('linear.videoMapOverrideHint')} />
                      </label>
                      <div className="space-y-2">
                        {resolutions.filter(r => selectedResolutions.includes(r.id)).map((res) => (
                          <div key={res.id} className="flex items-center gap-3 px-3 py-2 rounded-md border border-dark-700">
                            <span className="text-sm font-medium text-dark-200 w-20">{res.name}</span>
                            <input
                              type="text"
                              className="input flex-1 text-sm font-mono"
                              value={formData.video_map_overrides[res.name] || ''}
                              onChange={(e) => setFormData(prev => ({
                                ...prev,
                                video_map_overrides: { ...prev.video_map_overrides, [res.name]: e.target.value },
                              }))}
                              placeholder={res.xaccel_value}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Proxy per Resolution - Per-server blocks */}
                  {xaccelServers.length > 0 && formData.server_ids.length > 0 && selectedResolutions.length > 0 && (() => {
                    const selectedRes = resolutions.filter(r => selectedResolutions.includes(r.id));
                    return (
                    <div className="border-t border-dark-700 pt-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="label mb-0">
                          {t('proxies.proxyPerResolution')}
                          {' '}<HintTooltip text={t('proxies.proxyPerResolutionHint')} />
                        </label>
                        <button
                          type="button"
                          onClick={handleSyncProxies}
                          disabled={isSyncingProxies}
                          className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 disabled:opacity-50"
                        >
                          {isSyncingProxies ? (
                            <Loader2 size={14} className="animate-spin" />
                          ) : (
                            <RefreshCw size={14} />
                          )}
                          {t('proxies.sync')}
                        </button>
                      </div>
                      <div className="space-y-3">
                        {formData.server_ids.map((sId: number) => {
                          const server = xaccelServers.find(s => s.id === sId);
                          if (!server) return null;
                          const sProxies = xaccelProxies.filter(p => p.xaccelServerId === sId);
                          return (
                            <div key={sId} className="rounded-lg border border-dark-700 p-3">
                              <div className="text-xs font-medium text-dark-400 mb-2 flex items-center gap-2">
                                {server.name}
                                <span className="px-1.5 py-0.5 rounded bg-dark-600 text-dark-300">
                                  {serverTypeLabel((server as any).server_type)}
                                </span>
                                {sProxies.length > 0 && <span className="text-dark-500">({sProxies.length})</span>}
                              </div>
                              {sProxies.length === 0 ? (
                                <div className="text-xs text-dark-500 text-center py-2">
                                  {isSyncingProxies ? t('common.loading') : t('proxies.noProxiesSync')}
                                </div>
                              ) : (
                                <div className="space-y-1.5">
                                  {selectedRes.map((res) => {
                                    const currentProxyId = getProxyForServer(proxyAssignments, sId, res.name);
                                    return (
                                      <div key={res.id} className="flex items-center gap-3">
                                        <span className="text-sm font-medium text-dark-200 w-20">{res.name}</span>
                                        <select
                                          className="input flex-1 text-sm"
                                          value={currentProxyId || ''}
                                          onChange={(e) => handleProxyAssign(sId, res.name, e.target.value ? Number(e.target.value) : null)}
                                        >
                                          <option value="">{t('proxies.noProxy')}</option>
                                          {sProxies.map((proxy) => (
                                            <option key={proxy.id} value={proxy.id}>
                                              {proxy.name} ({proxy.host}:{proxy.port})
                                              {proxy.isOnline ? '' : ' - Offline'}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      <p className="text-xs text-dark-500 mt-2">{t('proxies.proxyAssignHelp')}</p>
                    </div>
                    );
                  })()}
                </div>
              )}

              {/* TAB: RESTART */}
              {activeTab === 'restart' && (
                <div className="space-y-6">
                  {/* Auto restart toggle */}
                  <div className="flex items-center justify-between p-3 rounded-lg border border-dark-700 bg-dark-900">
                    <div>
                      <div className="text-sm font-medium text-dark-200">
                        {t('linear.autoRestart')} <HintTooltip text={t('linear.autoRestartHint')} />
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.auto_restart_enabled}
                        onChange={e => setFormData(prev => ({ ...prev, auto_restart_enabled: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-dark-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Scheduled times */}
                  {formData.auto_restart_enabled && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <label className="label mb-0">
                          {t('linear.scheduledTimes')} <HintTooltip text={t('linear.scheduledTimesHint')} />
                        </label>
                        {restartTimesArray.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, restart_times: '[]' }))}
                            className="text-xs text-dark-500 hover:text-error-600 transition-colors"
                          >
                            {t('linear.removeAll')}
                          </button>
                        )}
                      </div>

                      {/* Time grid organized by rows of 4 hours */}
                      <div className="space-y-1.5">
                        {[0, 4, 8, 12, 16, 20].map(startHour => (
                          <div key={startHour} className="flex gap-1">
                            <span className="text-[10px] text-dark-500 w-5 flex-shrink-0 pt-1 text-right font-mono">{startHour.toString().padStart(2, '0')}</span>
                            <div className="flex flex-wrap gap-1 flex-1">
                              {Array.from({ length: 8 }, (_, i) => {
                                const h = startHour + Math.floor(i / 2);
                                const m = (i % 2) * 30;
                                const time = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
                                const isActive = restartTimesArray.includes(time);
                                return (
                                  <button
                                    key={time}
                                    type="button"
                                    onClick={() => toggleRestartTime(time)}
                                    className={`w-[52px] py-1 text-xs rounded border transition-all font-mono ${
                                      isActive
                                        ? 'bg-primary-600 text-white border-primary-600 font-medium'
                                        : 'bg-white text-dark-400 border-dark-700 hover:border-dark-500'
                                    }`}
                                  >
                                    {time}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {restartTimesArray.length > 0 && (
                        <div className="mt-3 p-2.5 rounded-md bg-dark-900 border border-dark-700">
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-dark-400">
                              <strong className="text-dark-300">{t('linear.activeTimesCount', { count: restartTimesArray.length })}</strong>
                            </span>
                            <span className="text-xs text-dark-500 font-mono">
                              {restartTimesArray.join(' · ')}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Manual time input */}
                      <div className="mt-4 pt-4 border-t border-dark-700">
                        <label className="label">
                          {t('linear.addManualTime')} <HintTooltip text={t('linear.addManualTimeHint')} />
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="time"
                            id="manual-restart-time"
                            className="input text-sm font-mono w-32"
                            step="60"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const input = document.getElementById('manual-restart-time') as HTMLInputElement;
                              const val = input?.value;
                              if (!val) { toast.error(t('linear.enterTime')); return; }
                              const [hh, mm] = val.split(':');
                              const time = `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
                              if (restartTimesArray.includes(time)) { toast.error(t('linear.timeAlreadyExists', { time })); return; }
                              toggleRestartTime(time);
                              input.value = '';
                              toast.success(t('linear.timeAdded', { time }));
                            }}
                            className="btn-secondary text-sm"
                          >
                            <Plus size={14} />
                            {t('common.add')}
                          </button>
                        </div>
                      </div>

                      {/* Health check auto-restart settings */}
                      <div className="mt-4 pt-4 border-t border-dark-700">
                        <label className="label mb-3">
                          {t('linear.healthRestartSettings')} <HintTooltip text={t('linear.healthRestartSettingsHint')} />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-dark-400 mb-1 block">{t('linear.maxRetries')}</label>
                            <input
                              type="number"
                              min={1}
                              max={10}
                              value={formData.max_restart_attempts}
                              onChange={e => setFormData(prev => ({ ...prev, max_restart_attempts: parseInt(e.target.value) || 3 }))}
                              className="input text-sm w-full"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-dark-400 mb-1 block">{t('linear.cooldownMinutes')}</label>
                            <input
                              type="number"
                              min={1}
                              max={60}
                              value={formData.restart_cooldown_minutes}
                              onChange={e => setFormData(prev => ({ ...prev, restart_cooldown_minutes: parseInt(e.target.value) || 10 }))}
                              className="input text-sm w-full"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Health restart counter display */}
                  {editingChannel && (editingChannel.health_restart_count ?? 0) > 0 && (
                    <div className="p-2.5 rounded-md bg-warning-50 border border-warning-200">
                      <span className="text-xs text-warning-700 font-medium">
                        {t('linear.healthRestartCount', { count: editingChannel.health_restart_count, max: editingChannel.max_restart_attempts ?? 3 })}
                      </span>
                    </div>
                  )}

                  {/* Last restart info + test button */}
                  <div className="pt-4 border-t border-dark-700 space-y-3">
                    {editingChannel?.last_restart_at && (
                      <div className="flex items-center gap-2 text-sm text-dark-400">
                        <Clock size={14} />
                        {t('linear.lastRestart')}: <span className="text-dark-300">{new Date(editingChannel.last_restart_at).toLocaleString()}</span>
                      </div>
                    )}

                    {editingChannel && editingChannel.status === 'running' && (
                      <button
                        type="button"
                        disabled={actionLoading[`${editingChannel.id}-restart`]}
                        onClick={() => askChannelAction(channels.find((c) => c.id === editingChannel.id) || editingChannel, 'restart', undefined, async () => {
                          const key = `${editingChannel.id}-restart`;
                          setActionLoading(prev => ({ ...prev, [key]: true }));
                          try {
                            await linearService.restartWithKeys(editingChannel.id);
                            toast.success(t('linear.restartWithKeysCompleted'));
                            fetchData();
                          } catch (error) {
                            toast.error(errText(error) || t('linear.restartError'));
                          } finally {
                            setActionLoading(prev => ({ ...prev, [key]: false }));
                          }
                        })}
                        className="btn-secondary text-sm w-full justify-center"
                      >
                        {actionLoading[`${editingChannel.id}-restart`]
                          ? <Loader2 size={14} className="animate-spin" />
                          : <RefreshCw size={14} />
                        }
                        {t('linear.testRestartNow')}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* TAB: CANALI XACCEL */}
              {activeTab === 'xaccel' && editingChannel && (
                <div className="space-y-5">
                  {/* Real state per server + scoped actions (phase 3): only on the ticked servers, preview + confirm */}
                  <LegsPanel
                    channelId={editingChannel.id}
                    actionTick={legActionTick}
                    openConfirm={setConfirmConfig}
                    onActed={() => fetchData()}
                    onLoaded={(list) => setLegCount(list.reduce((n, srv) => n + srv.rows.filter((r) => r.leg).length, 0))}
                  />
                  {/* Channel-wide tools that act on the DB legs (unchanged from before phase 3) */}
                  <div className="flex flex-wrap gap-2">
                    {legCount > 0 && (
                      <button
                        onClick={async () => {
                          const key = `${editingChannel!.id}-pushConfig`;
                          setActionLoading(prev => ({ ...prev, [key]: true }));
                          try {
                            // Save form first (without closing modal)
                            const vmOverridesPush = formData.video_map_overrides || {};
                            const hasOverridesPush = Object.values(vmOverridesPush).some(v => v && v.trim() !== '');
                            await linearService.update(editingChannel!.id, {
                              name: formData.name,
                              xaccel_channel_name: formData.xaccel_channel_name || undefined,
                              xaccel_server_id: formData.server_ids.length > 0 ? formData.server_ids[0] : (formData.xaccel_server_id ? parseInt(formData.xaccel_server_id) : undefined),
                            server_ids: formData.server_ids,
                              xaccel_stream_enable: formData.xaccel_stream_enable,
                              cdn_blacklist: JSON.stringify(formData.cdn_blacklist || []),
                              cdn_preferred: JSON.stringify(formData.cdn_preferred || []),
                              verify_age: formData.verify_age,
                              pin_protect: formData.pin_protect,
                              auto_restart_enabled: formData.auto_restart_enabled,
                              restart_times: formData.restart_times,
                              video_map_overrides: hasOverridesPush ? JSON.stringify(vmOverridesPush) : null,
                              max_restart_attempts: formData.max_restart_attempts,
                              restart_cooldown_minutes: formData.restart_cooldown_minutes,
                            });
                            // Save resolutions
                            clearTimeout(resolutionSaveTimer.current);
                            await linearService.updateResolutions(editingChannel!.id, selectedResolutions);
                            // Push config to Xaccel
                            const result = await linearService.pushConfig(editingChannel!.id);
                            if (result.failed) {
                              toast.error(t('linear.configPushPartial', { updated: result.updated, total: result.total, failed: result.failed, names: (result.failures || []).map((f) => `${f.channelName}: ${f.error}`).join('; ') }), { duration: 10000 });
                            } else {
                              toast.success(t('linear.configSavedAndPushed', { updated: result.updated, total: result.total }));
                            }
                            fetchData();
                            bumpLegState();
                          } catch (error) {
                            toast.error(errText(error) || t('linear.pushConfigError'));
                          } finally {
                            setActionLoading(prev => ({ ...prev, [key]: false }));
                          }
                        }}
                        disabled={!!actionLoading[`${editingChannel!.id}-pushConfig`]}
                        className="btn-secondary text-sm"
                      >
                        {actionLoading[`${editingChannel!.id}-pushConfig`]
                          ? <Loader2 size={14} className="animate-spin" />
                          : <Upload size={14} />
                        }
                        {t('linear.updateConfig')}
                      </button>
                    )}
                    {legCount > 0 && (
                      <button
                        onClick={async () => {
                          const key = `${editingChannel!.id}-syncStatus`;
                          setActionLoading(prev => ({ ...prev, [key]: true }));
                          try {
                            const result = await linearService.syncStatus(editingChannel!.id);
                            if (result.parentStatus === null) toast.error(t('linear.statusSyncNoServer'));
                            else toast.success(t('linear.statusSynced', { synced: result.synced, status: result.parentStatus }));
                            fetchData();
                            bumpLegState();
                          } catch (error) {
                            toast.error(errText(error) || t('common.error'));
                          } finally {
                            setActionLoading(prev => ({ ...prev, [key]: false }));
                          }
                        }}
                        disabled={!!actionLoading[`${editingChannel!.id}-syncStatus`]}
                        className="btn-secondary text-sm"
                      >
                        {actionLoading[`${editingChannel!.id}-syncStatus`]
                          ? <Loader2 size={14} className="animate-spin" />
                          : <RefreshCw size={14} />
                        }
                        {t('linear.syncStatus')}
                      </button>
                    )}
                    <button
                      onClick={async () => {
                        if (!editingChannel) return;
                        const key = `${editingChannel.id}-syncChannels`;
                        setActionLoading(prev => ({ ...prev, [key]: true }));
                        try {
                          const result = await linearService.syncChannels(editingChannel.id);
                          if (result.created > 0) {
                            toast.success(t('linear.channelsSynced', { created: result.created }));
                            bumpLegState();
                            fetchData();
                          } else {
                            toast.success(t('linear.channelsSyncNoNew'));
                          }
                        } catch (error) {
                          toast.error(errText(error) || t('common.error'));
                        } finally {
                          setActionLoading(prev => ({ ...prev, [key]: false }));
                        }
                      }}
                      disabled={!!actionLoading[`${editingChannel!.id}-syncChannels`]}
                      className="btn-secondary text-sm"
                    >
                      {actionLoading[`${editingChannel!.id}-syncChannels`]
                        ? <Loader2 size={14} className="animate-spin" />
                        : <Download size={14} />
                      }
                      {t('linear.syncChannels')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-dark-700 flex-shrink-0">
              <button onClick={closeModal} className="btn-secondary">
                {t('common.cancel')}
              </button>
              <button
                onClick={editingChannel ? saveChannel : async () => {
                  if (!formData.name || !formData.dazn_asset_id) {
                    toast.error(t('linear.nameAndAssetRequired'));
                    return;
                  }
                  setIsSaving(true);
                  try {
                    const created = await linearService.create({
                      name: formData.name,
                      dazn_asset_id: formData.dazn_asset_id,
                      xaccel_channel_name: formData.xaccel_channel_name || undefined,
                      xaccel_server_id: formData.server_ids.length > 0 ? formData.server_ids[0] : (formData.xaccel_server_id ? parseInt(formData.xaccel_server_id) : undefined),
                      server_ids: formData.server_ids,
                      auto_restart_enabled: formData.auto_restart_enabled,
                      restart_times: formData.restart_times,
                      max_restart_attempts: formData.max_restart_attempts,
                      restart_cooldown_minutes: formData.restart_cooldown_minutes,
                    });
                    toast.success(t('linear.createdNextStep'), { duration: 8000 });
                    fetchData();
                    // Nothing is created on the servers yet: the channel opens on its streaming settings, then the
                    // Xaccel tab shows the real state per server and creates only what is missing on the ticked ones.
                    await openEditModal(created);
                    setActiveTab('streaming');
                  } catch (error) {
                    toast.error(errText(error) || t('common.error'));
                  } finally {
                    setIsSaving(false);
                  }
                }}
                disabled={isSaving}
                className="btn-primary"
              >
                {isSaving ? <Loader2 size={16} className="animate-spin" /> : null}
                {t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmActionModal config={confirmConfig} onClose={(closing) => setConfirmConfig((cur) => (closeAfterRun(closing, cur) ? null : cur))} />
    </div>
  );
}
